import { apiFetch } from "@/lib/api";
import type {
  NavigationItemStateDto,
  NavigationSurfaceName,
  WorkspaceNavigationDto,
} from "@/lib/types";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRegistrations } from "./registrations-provider";
import { useSession } from "./session-provider";
import { useWorkspace } from "./workspace-provider";

interface NavigationState {
  /** The arrangement for one rail, or null while it is unknown or unarranged. */
  arrangementFor: (surface: NavigationSurfaceName) => NavigationItemStateDto[] | null;
  /** The workspace whose arrangement is in force, for the admin editor to open on. */
  workspaceId: string | null;
}

const NavigationContext = createContext<NavigationState>({
  arrangementFor: () => null,
  workspaceId: null,
});

/**
 * Fetches the navigation arrangement for the workspace the signed-in person is looking at.
 *
 * The workspace is the selected one for anybody managing workspaces, and otherwise the one behind
 * their active challenge. Participants have no selected workspace — `WorkspaceProvider` only
 * fetches for administrators and workspace managers — so the registration is the only thing that
 * can name it.
 */
export function NavigationProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const { selectedWorkspaceId } = useWorkspace();
  const { registrations, activeSlug } = useRegistrations();

  const workspaceId = useMemo(() => {
    if (selectedWorkspaceId) return selectedWorkspaceId;
    const active = registrations.find(
      (registration) => registration.challengeSlug === activeSlug,
    );
    return active?.workspaceId ?? null;
  }, [selectedWorkspaceId, registrations, activeSlug]);

  const [arrangements, setArrangements] = useState<WorkspaceNavigationDto | null>(null);

  useEffect(() => {
    if (me?.authenticated !== true || !workspaceId) {
      setArrangements(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch<WorkspaceNavigationDto>(
          `/api/navigation/${workspaceId}`,
          { suppressAuthRedirect: true },
        );
        if (!cancelled) setArrangements(data ?? null);
      } catch {
        // A rail that cannot be read is not a reason to show no rail: falling back to the shipped
        // navigation keeps every destination reachable.
        if (!cancelled) setArrangements(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [me?.authenticated, workspaceId]);

  const value = useMemo<NavigationState>(
    () => ({
      workspaceId,
      arrangementFor: (surface) => {
        const found = arrangements?.surfaces.find((item) => item.surface === surface);
        return found?.customised ? found.arrangement : null;
      },
    }),
    [arrangements, workspaceId],
  );

  return (
    <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
  );
}

export function useNavigationArrangement() {
  return useContext(NavigationContext);
}
